import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { invitationAPI } from '@/services/api';
import { CalendarCheck, Loader2, User, Lock, Eye, EyeOff, CheckCircle2, AlertCircle, ArrowRight } from 'lucide-react';

export default function InviteSetup() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    loadInvite();
  }, [token]);

  const loadInvite = async () => {
    try {
      const res = await invitationAPI.getDetails(token);
      if (res.data.hasAccount) {
        // Existing users shouldn't be here — send them to the accept page
        navigate(`/accept-invite/${token}`, { replace: true });
        return;
      }
      setInvite(res.data);
      setName(res.data.email.split('@')[0]);
    } catch (err) {
      setError(err.response?.data?.message || 'Invitation not found or expired');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      await invitationAPI.accept(token, {
        name: name.trim(),
        password
      });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || 'Could not set up account');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--dash-bg)' }}>
        <div className="flex items-center gap-3" style={{ color: 'var(--dash-text3)' }}>
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  if (error && !invite) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'var(--dash-bg)' }}>
        <div className="w-full max-w-md rounded-2xl p-8 text-center" style={{ background: 'var(--dash-bg2)', border: '1px solid var(--dash-border)' }}>
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(248,113,113,.1)' }}>
            <AlertCircle className="w-6 h-6" style={{ color: '#f87171' }} />
          </div>
          <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--dash-text)' }}>{error}</h2>
          <p className="text-sm mb-6" style={{ color: 'var(--dash-text3)' }}>
            This invitation may have expired, already been used, or the link is invalid.
          </p>
          <Link to="/" className="inline-flex items-center justify-center w-full h-10 rounded-lg text-sm font-semibold transition-opacity hover:opacity-90" style={{ background: 'var(--dash-grad-btn)', color: 'var(--dash-bg)' }}>
            Go to Home
          </Link>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'var(--dash-bg)' }}>
        <div className="w-full max-w-md rounded-2xl p-8 text-center" style={{ background: 'var(--dash-bg2)', border: '1px solid var(--dash-border)' }}>
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(74,222,128,.1)' }}>
            <CheckCircle2 className="w-6 h-6" style={{ color: '#4ade80' }} />
          </div>
          <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--dash-text)' }}>Password Set Successfully!</h2>
          <p className="text-sm mb-6" style={{ color: 'var(--dash-text3)' }}>
            Your account for {invite.teamName} is ready. Log in to access the dashboard.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="inline-flex items-center justify-center gap-2 w-full h-10 rounded-lg text-sm font-semibold transition-opacity hover:opacity-90"
            style={{ background: 'var(--dash-grad-btn)', color: 'var(--dash-bg)' }}
          >
            Go to Login <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" style={{ background: 'var(--dash-bg)' }}>
      <div className="w-full max-w-md rounded-2xl p-8" style={{ background: 'var(--dash-bg2)', border: '1px solid var(--dash-border)' }}>
        <div className="text-center mb-6">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-3" style={{ background: 'var(--dash-grad-btn)' }}>
            <CalendarCheck className="w-5 h-5" style={{ color: 'var(--dash-bg)' }} />
          </div>
          <h1 className="text-xl font-bold" style={{ color: 'var(--dash-text)' }}>Set Your Password</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--dash-text3)' }}>
            You're joining <span className="font-semibold" style={{ color: 'var(--dash-text)' }}>{invite.teamName}</span> as a <span className="capitalize">{invite.role}</span>
          </p>
        </div>

        {error && (
          <div className="mb-4 flex items-center gap-2 rounded-lg p-3 text-sm" style={{ background: 'rgba(248,113,113,.1)', color: '#f87171' }}>
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--dash-text3)' }}>Full Name</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--dash-text4)' }} />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full h-10 rounded-lg pl-10 pr-3 text-sm outline-none transition-colors"
                style={{ background: 'var(--dash-input-bg)', border: '1px solid var(--dash-input-border)', color: 'var(--dash-text)' }}
                placeholder="Your name"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--dash-text3)' }}>Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--dash-text4)' }} />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="w-full h-10 rounded-lg pl-10 pr-10 text-sm outline-none transition-colors"
                style={{ background: 'var(--dash-input-bg)', border: '1px solid var(--dash-input-border)', color: 'var(--dash-text)' }}
                placeholder="Create a password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: 'var(--dash-text4)' }}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-[11px] mt-1" style={{ color: 'var(--dash-text4)' }}>At least 6 characters</p>
          </div>

          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--dash-text3)' }}>Confirm Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--dash-text4)' }} />
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                className="w-full h-10 rounded-lg pl-10 pr-3 text-sm outline-none transition-colors"
                style={{ background: 'var(--dash-input-bg)', border: '1px solid var(--dash-input-border)', color: 'var(--dash-text)' }}
                placeholder="Re-enter password"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full h-10 rounded-lg text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ background: 'var(--dash-grad-btn)', color: 'var(--dash-bg)' }}
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Setting up...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4" />
                Create Account & Join Team
              </>
            )}
          </button>
        </form>

        <p className="text-center text-[11px] mt-6" style={{ color: 'var(--dash-text5)' }}>
          Invitation expires on {new Date(invite.expiresAt).toLocaleDateString()}
        </p>
      </div>
    </div>
  );
}
